import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import Map, { NavigationControl, ScaleControl } from 'react-map-gl'
import mapboxgl from 'mapbox-gl'
import { FaChevronDown, FaChevronUp, FaChevronLeft } from 'react-icons/fa' // Import icons for toggle buttons
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import './Map.css'

import TableResultsComponent from '../TableResults'

// Make sure the token is properly defined
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_KEY

const styleOptions = [
  {
    label: 'Street',
    style: 'mapbox://styles/mapbox/standard',
    drawColor: '#1a73e8',
    thickness: 2,
  },
  {
    label: 'Hybrid',
    style: 'mapbox://styles/mapbox/satellite-streets-v12',
    drawColor: '#00ff88',
    thickness: 3,
  },
  {
    label: 'Satellite',
    style: 'mapbox://styles/mapbox/satellite-v9',
    drawColor: '#ffff00',
    thickness: 4,
  },
  {
    label: 'Terrain',
    style: 'mapbox://styles/mapbox/outdoors-v12',
    drawColor: '#ff3b30',
    thickness: 3,
  },
  {
    label: 'Dark',
    style: 'mapbox://styles/mapbox/dark-v11',
    drawColor: '#00ffff',
    thickness: 2,
  },
  {
    label: 'Light',
    style: 'mapbox://styles/mapbox/light-v11',
    drawColor: '#5856d6',
    thickness: 2,
  },
]

// Isolated Map component to prevent unnecessary re-renders
const MemoizedMap = React.memo(
  React.forwardRef(
    ({ initialViewState, style, mapStyle, onLoad, onError }, ref) => {
      // console.log('MemoizedMap render')
      return (
        <Map
          mapLib={mapboxgl}
          ref={ref}
          initialViewState={initialViewState}
          style={style}
          mapStyle={mapStyle}
          mapboxAccessToken={MAPBOX_TOKEN}
          onLoad={onLoad}
          onError={onError}
        >
          <NavigationControl position="top-left" />
          <ScaleControl position="bottom-left" unit="imperial" />
        </Map>
      )
    }
  )
)

MemoizedMap.displayName = 'MemoizedMap'

// Memoized TableResults to prevent duplicate API calls
const MemoizedTableResults = React.memo(
  ({ submittedSearchTerm, path, ssp, token }) => {
    // console.log(
    //   'TableResults render with term:',
    //   submittedSearchTerm ? 'data present' : 'no data'
    // )
    // console.log('Token being passed to TableResults:', token)
    return (
      <TableResultsComponent
        submittedSearchTerm={submittedSearchTerm}
        path={path}
        ssp={ssp}
        token={token}
      />
    )
  }
)

MemoizedTableResults.displayName = 'MemoizedTableResults'

const MapExtComponent = React.memo(
  ({ data, err, path, ssp, mapStyleProp, token, onBack }) => {
    const [isResultsExpanded, setIsResultsExpanded] = useState(true)
    const mapRef = useRef(null)
    const [mapStyle, setMapStyle] = useState(
      mapStyleProp || styleOptions[0].style
    )
    const [polygonColor, setPolygonColor] = useState(
      styleOptions.find(
        (option) => option.style === (mapStyleProp || styleOptions[0].style)
      )?.drawColor || '#00ff00'
    )
    const [isLoading, setIsLoading] = useState(false)
    const [mapError, setMapError] = useState(null)
    const [isMapExpanded, setIsMapExpanded] = useState(true)

    // Use the data directly since it's now the original object
    const mapData = useMemo(() => {
      return data || null
    }, [data])

    // Encode the data for the API request
    const encodedDataForAPI = useMemo(() => {
      if (!data) return null
      try {
        return btoa(JSON.stringify(data)).replace(/\//g, '_').replace(/\+/g, '-').replace(/=+$/, '')
      } catch (error) {
        console.error('Error encoding data for API:', error)
        return null
      }
    }, [data])

    // Memoize the data JSON string to prevent unnecessary re-renders of TableResultsComponent
    const memoizedDataString = useMemo(() => {
      return encodedDataForAPI
    }, [encodedDataForAPI])

    // Debug logging
    useEffect(() => {
      // console.log('MapExtComponent mounted')
      // console.log('MAPBOX_TOKEN available:', !!MAPBOX_TOKEN)
      // console.log('Initial mapStyle:', mapStyle)

      // Check if the container exists and has dimensions
      const checkContainer = () => {
        const container = document.querySelector('.map-container')
        if (container) {
          // console.log('Map container dimensions:', {
          //   width: container.offsetWidth,
          //   height: container.offsetHeight,
          // })
        } else {
          // console.warn('Map container not found in DOM')
        }
      }

      // Check after a short delay to ensure DOM is ready
      setTimeout(checkContainer, 500)

      return () => {
        // console.log('MapExtComponent unmounting')
      }
    }, [])

    // Update mapStyle when mapStyleProp changes
    useEffect(() => {
      if (mapStyleProp) {
        // console.log('mapStyleProp changed:', mapStyleProp)
        setMapStyle(mapStyleProp)
        const styleOption = styleOptions.find(
          (option) => option.style === mapStyleProp
        )
        if (styleOption) {
          setPolygonColor(styleOption.drawColor)
        }
      }
    }, [mapStyleProp])

    const handleMapLoad = useCallback(() => {
      // console.log('Map loaded callback triggered')
      setIsLoading(true)

      if (!mapRef || !mapRef.current) {
        // console.warn('mapRef is not available')
        return
      }

      try {
        const map = mapRef.current.getMap()
        // console.log('Map instance obtained successfully')

        if (mapData) {
          console.log('Processing data for map:', mapData)
          
          // Validate that the data has the expected structure
          if (!mapData.coordinates || !Array.isArray(mapData.coordinates) || mapData.coordinates.length === 0) {
            throw new Error('Invalid data structure: missing or empty coordinates array')
          }
          
          if (!mapData.coordinates[0] || !Array.isArray(mapData.coordinates[0]) || mapData.coordinates[0].length === 0) {
            throw new Error('Invalid data structure: missing or empty first coordinate array')
          }
          
          // Calculate the bounding box of the polygon
          const bounds = mapData.coordinates[0].reduce(
            (bounds, coord) => bounds.extend(coord),
            new mapboxgl.LngLatBounds(
              mapData.coordinates[0][0],
              mapData.coordinates[0][0]
            )
          )

          const expandedBounds = bounds
            .extend([
              bounds.getNorthEast().lng + 0.1,
              bounds.getNorthEast().lat + 0.1,
            ])
            .extend([
              bounds.getSouthWest().lng - 0.1,
              bounds.getSouthWest().lat - 0.1,
            ])

          // Fit the map to the bounding box
          map.fitBounds(expandedBounds, {
            padding: 20,
            maxZoom: 15,
            minZoom: 8,
          })

          const geojson = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: mapData,
                properties: {},
              },
            ],
          }

          // Add the polygon to the map
          if (!map.getSource('polygon-source')) {
            map.addSource('polygon-source', {
              type: 'geojson',
              data: geojson,
            })

            map.addLayer({
              id: 'polygon-fill',
              type: 'fill',
              source: 'polygon-source',
              paint: {
                'fill-color': polygonColor,
                'fill-opacity': 0.2,
              },
            })

            map.addLayer({
              id: 'polygon-outline',
              type: 'line',
              source: 'polygon-source',
              paint: {
                'line-color': polygonColor,
                'line-width': 2,
              },
            })
          } else {
            // Update the source if it already exists
            map.getSource('polygon-source').setData(geojson)

            // Update the polygon colors
            map.setPaintProperty('polygon-fill', 'fill-color', polygonColor)
            map.setPaintProperty('polygon-outline', 'line-color', polygonColor)
          }
        }
      } catch (error) {
        // console.error('Error in handleMapLoad:', error)
        setMapError(error.message)
      } finally {
        setIsLoading(false)
      }
    }, [mapData, polygonColor])

    // Memoize the error handler function
    const handleMapError = useCallback((e) => {
      // console.error('Map error:', e)
      setMapError(e.error?.message || 'Unknown map error')
    }, [])

    // Toggle functions for expanding/collapsing sections
    const toggleMapSection = useCallback(() => {
      setIsMapExpanded(!isMapExpanded)
      // Resize map after toggling to ensure proper rendering
      setTimeout(() => {
        if (mapRef.current) {
          try {
            const map = mapRef.current.getMap()
            map.resize()
          } catch (error) {
            // console.error('Error resizing map:', error)
          }
        }
      }, 300)
    }, [isMapExpanded])

    const toggleResultsSection = useCallback(() => {
      setIsResultsExpanded(!isResultsExpanded)
    }, [isResultsExpanded])

    // Memoize the initial view state object to prevent recreation on every render
    const initialViewState = useMemo(
      () => ({
        longitude: -99.9018,
        latitude: 31.9686,
        zoom: 6,
      }),
      []
    )

    // Memoize the map style object to prevent recreation on every render
    const mapContainerStyle = useMemo(
      () => ({
        width: '100%',
        height: '100%',
      }),
      []
    )

    return (
      <div className="search-container-1 map-ext">
        {isLoading && (
          <div className="map-loading-overlay">
            <div className="spinner"></div>
          </div>
        )}

        {/* Selected Area Card */}
        <div className="content-card global-content-card">
          <div className="card-header">
            <div className="card-title-container">
              <span className="card-title">Map Search</span>
              <button 
                className="back-button" 
                onClick={onBack}
                aria-label="Go back"
              >
                <FaChevronLeft className="back-icon" />
                <FaChevronLeft className="back-icon" style={{ marginLeft: '-10px' }} />
                <FaChevronLeft className="back-icon" style={{ marginLeft: '-10px' }} />
              </button>
            </div>
            <div className="accordion-controls">
              <button
                className={`toggle-btn ${isMapExpanded ? 'expanded' : 'collapsed'}`}
                onClick={() => setIsMapExpanded(!isMapExpanded)}
                aria-label={isMapExpanded ? 'Collapse map' : 'Expand map'}
              >
                {isMapExpanded ? (
                  <FaChevronUp className="up-rotate" />
                ) : (
                  <FaChevronDown className="down-rotate" />
                )}
              </button>
            </div>
          </div>
          <div
            className={`card-body ${isMapExpanded ? 'expanded' : 'collapsed'}`}
          >
            {isMapExpanded && (
              <div
                className="map-container"
                style={{ height: '400px', width: '100%' }}
              >
                {mapError ? (
                  <div className="map-error">
                    <p>Error loading map: {mapError}</p>
                    <button onClick={() => window.location.reload()}>
                      Reload Page
                    </button>
                  </div>
                ) : (
                  <MemoizedMap
                    ref={mapRef}
                    initialViewState={initialViewState}
                    style={mapContainerStyle}
                    mapStyle={mapStyle}
                    onLoad={handleMapLoad}
                    onError={handleMapError}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Results Card */}
        {mapData && (
          <div className="content-card">
            <div className="card-header">
              <span className="card-title">Results</span>
              <div className="accordion-controls">
                <button
                  className={`toggle-btn ${
                    isResultsExpanded ? 'expanded' : 'collapsed'
                  }`}
                  onClick={toggleResultsSection}
                  aria-label={
                    isResultsExpanded ? 'Collapse results' : 'Expand results'
                  }
                >
                  {isResultsExpanded ? (
                    <FaChevronUp className="up-rotate" />
                  ) : (
                    <FaChevronDown className="down-rotate" />
                  )}
                </button>
              </div>
            </div>
            <div
              className={`card-body ${
                isResultsExpanded ? 'expanded' : 'collapsed'
              }`}
            >
              <MemoizedTableResults
                submittedSearchTerm={memoizedDataString}
                path={path}
                ssp={ssp}
                token={token}
              />
            </div>
          </div>
        )}
      </div>
    )
  }
)

export default MapExtComponent
